import axios, { HttpStatusCode } from 'axios';
import {
  ArticleListResponse,
  ArticleParams,
  ArticleResponse,
  defaultContentTypes,
} from '../types/types';
import { primaryCategories } from '../utilities/Categories';
import { TujuAccountInformation } from '@almamedia-legal-insights/types';

export const instance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
});

export async function getArticle(
  id: string,
  auth?: string,
): Promise<ArticleResponse> {
  const axiosResponse = await instance.get(`/articles/${id}`, {
    headers: {
      ...(auth && {
        Authorization: `Bearer ${auth}`,
      }),
    },
  });

  if (axiosResponse.status === HttpStatusCode.Ok && axiosResponse.data) {
    return axiosResponse.data;
  } else {
    throw new Error('Unable to fetch the article');
  }
}

export async function getArticles({
  page = 1,
  limit = 10,
  contentType = defaultContentTypes,
  category,
  author,
  orderBy,
  includeUpcoming = false,
  upcoming = false,
}: ArticleParams): Promise<ArticleListResponse> {
  const isPrimaryCategory = category
    ? Object.values(primaryCategories).includes(category)
    : false;

  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    includeUpcoming: includeUpcoming.toString(),
    upcoming: upcoming.toString(),
    ...(category && {
      [isPrimaryCategory ? 'categoryPrimary' : 'categorySecondary']: category,
    }),
  });

  const optionalParams: { [key: string]: string | undefined } = {
    author,
    orderBy,
  };

  for (const [key, value] of Object.entries(optionalParams)) {
    if (value) {
      params.append(key, value);
    }
  }

  if (contentType && Array.isArray(contentType)) {
    contentType.forEach((type) => params.append('contentType', type));
  } else {
    params.append('contentType', contentType);
  }

  const axiosResponse = await instance.get('/articles', { params });

  if (axiosResponse.status === HttpStatusCode.Ok && axiosResponse.data) {
    return axiosResponse.data;
  } else {
    throw new Error('Unable to fetch articles');
  }
}

export async function searchArticles(
  keyword: string,
  page: number,
  limit = 10,
  contentType: string[] = defaultContentTypes,
  orderBy?: string,
): Promise<ArticleListResponse> {
  const params = new URLSearchParams({
    keyword,
    limit: limit.toString(),
    page: page.toString(),
    ...(orderBy && {
      orderBy,
    }),
  });
  contentType.forEach((type) => params.append('contentType', type));
  const axiosResponse = await instance.get(`/articles/search`, {
    params,
  });

  if (axiosResponse.status === HttpStatusCode.Ok && axiosResponse.data) {
    return axiosResponse.data;
  }

  throw new Error('Unable to search articles');
}

export async function getCategories() {
  const axiosResponse = await instance.get(`/categoryList`);

  if (axiosResponse.status === HttpStatusCode.Ok && axiosResponse.data) {
    return axiosResponse.data;
  } else {
    throw new Error('Unable to fetch categories');
  }
}

export async function getRegulationFeed(auth?: string): Promise<string> {
  const axiosResponse = await instance.get(`/regulationFeedList`, {
    params: {},
    headers: {
      ...(auth && {
        Authorization: `Bearer ${auth}`,
      }),
    },
  });

  // data can be an empty string
  if (
    axiosResponse.status === HttpStatusCode.Ok &&
    axiosResponse.data !== undefined
  ) {
    return axiosResponse.data;
  } else {
    throw new Error('Unable to fetch regulation feed');
  }
}

export async function getAccountInformation(
  auth?: string,
): Promise<TujuAccountInformation> {
  const axiosResponse = await instance.get(`/account`, {
    headers: {
      ...(auth && {
        Authorization: `Bearer ${auth}`,
      }),
    },
  });

  if (axiosResponse.status === HttpStatusCode.Ok && axiosResponse.data) {
    return axiosResponse.data as TujuAccountInformation;
  } else {
    throw new Error('Unable to fetch account information');
  }
}

export async function getAuthors() {
  const axiosResponse = await instance.get(`/authorList`);

  if (axiosResponse.status === HttpStatusCode.Ok && axiosResponse.data) {
    // Timehouse endpoint sometimes returns multiple authors separated by a pipe "|", hence the splitting here.
    return axiosResponse.data
      .flatMap((item: string) => item.split('|'))
      .filter(
        (value: string, index: number, self: string[]) =>
          self.indexOf(value) === index,
      );
  } else {
    throw new Error('Unable to fetch authors');
  }
}
